import React from 'react';
import { bool, object, shape, string, number } from 'prop-types';
import { StyledProductCard } from './styles';
import { getProductImg } from '../../utils/url';
import useFormatPrice from '../../utils/hooks/formatPriceHook';
import { AnalyticsContext } from '../../context/analyticsContext';

/**
 * A styled product card complete with a product image, price and information surrounding the
 * product and a reference, which is to be used to focus on direct for accessibility purposes.
 * @param {object} props
 * @param {object} props.product contains details surrounding this product for the consumer to see.
 * @param {string} props.searchTerm search term used to derive this product card as a result.
 * @param {object} props.searchResultsRef reference to be used for accessibility purposes.
 * @param {string} props.resultsLength length of collection results.
 * @param {object} props.index index within the search collection.
 * @returns
 */
export default function RecentlyViewedProductCard({ product, searchResultsRef, useSquarishOnly }) {
  const {
    copy,
    prices,
    pdpUrl,
    globalProductId: productId,
    featuredAttributes,
    colorwayImages,
  } = product;
  const { title } = copy;
  const { url: href } = pdpUrl;
  const nikeId = featuredAttributes?.includes('CUSTOMIZED');

  const { price, discountPrice } = useFormatPrice(prices);
  const { analyticsClient } = React.useContext(AnalyticsContext);

  const handleClick = () => {
    analyticsClient?.recentlyViewedProductClicked({ productId });
  };

  // Use squarishURL for all images if useSquarishOnly is true
  const imageSrc = useSquarishOnly ? colorwayImages?.squarishURL : getProductImg(product);

  return (
    <li>
      <StyledProductCard
        imageAspectRatio={nikeId || useSquarishOnly ? '1/1' : '4/5'}
        onClick={handleClick}
        products={[
          {
            hero: {
              image: {
                src: imageSrc,
                alt: title,
              },
            },
            details: {
              title,
              price,
              discountPrice,
            },
            link: {
              href,
              title,
            },
          },
        ]}
        ref={searchResultsRef}
      />
    </li>
  );
}

RecentlyViewedProductCard.propTypes = {
  product: shape({
    copy: shape({
      title: string,
      subTitle: string,
    }),
    globalProductId: string,
    prices: shape({
      currency: string,
      currentPrice: number,
      initialPrice: number,
      discountPercentage: number,
    }),
    colorwayImages: shape({
      squarishURL: string,
      portraitURL: string,
    }),
    pdpUrl: shape({
      url: string,
    }),
  }).isRequired,
  searchTerm: string,
  searchResultsRef: object,
  resultsLength: number,
  index: number,
  useSquarishOnly: bool,
};
